import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth, API } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  ChevronLeft, Save, Loader2, User, Building, MapPin, Phone, Mail, Shield, CheckCircle
} from 'lucide-react';

const CustomerProfile = () => {
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disclaimerStatus, setDisclaimerStatus] = useState({ accepted: false, accepted_at: null });
  const [profile, setProfile] = useState({
    // Personal Info
    name: '',
    email: '',
    phone: '',
    // Business Info
    company_name: '',
    abn: '',
    // Billing Address
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_postcode: '',
    // Shipping Address
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    shipping_postcode: '',
    shipping_same_as_billing: true,
    // Notes
    notes: ''
  });

  useEffect(() => {
    fetchProfile();
    fetchDisclaimerStatus();
  }, []);

  const fetchDisclaimerStatus = async () => {
    try {
      const response = await axios.get(`${API}/user/disclaimer-status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      setDisclaimerStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch disclaimer status:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.data) {
        setProfile(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      // Profile might not exist yet, use defaults from user
      if (user) {
        setProfile(prev => ({
          ...prev,
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || ''
        }));
      }
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/user/profile`, profile, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      toast.success('Profile updated successfully');
      // Update user context with new name if changed
      if (profile.name !== user?.name) {
        setUser(prev => ({ ...prev, name: profile.name }));
      }
    } catch (error) {
      toast.error('Failed to update profile');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Link to="/dashboard">
              <Button variant="outline" size="icon" className="border-white/20 text-white hover:bg-white/5 rounded-none">
                <ChevronLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <span className="overline text-[#CCFF00]">Profile</span>
              <h1 className="text-2xl font-['Chivo'] font-black text-white">My Profile</h1>
            </div>
          </div>

          {/* Personal Information */}
          <div className="bg-[#111] border border-white/10 rounded-sm mb-6">
            <div className="p-6 border-b border-white/10 flex items-center gap-3">
              <User className="w-5 h-5 text-[#CCFF00]" />
              <h2 className="text-lg font-bold text-white">Personal Information</h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-white/70">Full Name *</Label>
                <Input
                  name="name"
                  value={profile.name}
                  onChange={handleChange}
                  className="bg-white/5 border-white/10 text-white mt-2"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label className="text-white/70">Email *</Label>
                <Input
                  name="email"
                  type="email"
                  value={profile.email}
                  onChange={handleChange}
                  className="bg-white/5 border-white/10 text-white mt-2"
                  placeholder="john@example.com"
                  disabled
                />
                <p className="text-white/30 text-xs mt-1">Email cannot be changed</p>
              </div>
              <div>
                <Label className="text-white/70">Phone</Label>
                <Input
                  name="phone"
                  value={profile.phone}
                  onChange={handleChange}
                  className="bg-white/5 border-white/10 text-white mt-2"
                  placeholder="+61 400 123 456"
                />
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div className="bg-[#111] border border-white/10 rounded-sm mb-6">
            <div className="p-6 border-b border-white/10 flex items-center gap-3">
              <Building className="w-5 h-5 text-[#CCFF00]" />
              <h2 className="text-lg font-bold text-white">Business Information</h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-white/70">Company Name</Label>
                <Input
                  name="company_name"
                  value={profile.company_name}
                  onChange={handleChange}
                  className="bg-white/5 border-white/10 text-white mt-2"
                  placeholder="ABC Pty Ltd"
                />
              </div>
              <div>
                <Label className="text-white/70">ABN</Label>
                <Input
                  name="abn"
                  value={profile.abn}
                  onChange={handleChange}
                  className="bg-white/5 border-white/10 text-white mt-2"
                  placeholder="12 345 678 901"
                />
              </div>
            </div>
          </div>

          {/* Billing Address */}
          <div className="bg-[#111] border border-white/10 rounded-sm mb-6">
            <div className="p-6 border-b border-white/10 flex items-center gap-3">
              <MapPin className="w-5 h-5 text-[#CCFF00]" />
              <h2 className="text-lg font-bold text-white">Billing Address</h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label className="text-white/70">Street Address</Label>
                <Input
                  name="billing_address"
                  value={profile.billing_address}
                  onChange={handleChange}
                  className="bg-white/5 border-white/10 text-white mt-2"
                  placeholder="123 Main Street"
                />
              </div>
              <div>
                <Label className="text-white/70">City</Label>
                <Input
                  name="billing_city"
                  value={profile.billing_city}
                  onChange={handleChange}
                  className="bg-white/5 border-white/10 text-white mt-2"
                  placeholder="Melbourne"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/70">State</Label>
                  <Input
                    name="billing_state"
                    value={profile.billing_state}
                    onChange={handleChange}
                    className="bg-white/5 border-white/10 text-white mt-2"
                    placeholder="VIC"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Postcode</Label>
                  <Input
                    name="billing_postcode"
                    value={profile.billing_postcode}
                    onChange={handleChange}
                    className="bg-white/5 border-white/10 text-white mt-2"
                    placeholder="3000"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="bg-[#111] border border-white/10 rounded-sm mb-6">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-[#CCFF00]" />
                <h2 className="text-lg font-bold text-white">Shipping Address</h2>
              </div>
              <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  name="shipping_same_as_billing"
                  checked={profile.shipping_same_as_billing}
                  onChange={handleChange}
                  className="rounded border-white/20 bg-white/5"
                />
                Same as billing
              </label>
            </div>
            {!profile.shipping_same_as_billing && (
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Label className="text-white/70">Street Address</Label>
                  <Input
                    name="shipping_address"
                    value={profile.shipping_address}
                    onChange={handleChange}
                    className="bg-white/5 border-white/10 text-white mt-2"
                    placeholder="123 Main Street"
                  />
                </div>
                <div>
                  <Label className="text-white/70">City</Label>
                  <Input
                    name="shipping_city"
                    value={profile.shipping_city}
                    onChange={handleChange}
                    className="bg-white/5 border-white/10 text-white mt-2"
                    placeholder="Melbourne"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70">State</Label>
                    <Input
                      name="shipping_state"
                      value={profile.shipping_state}
                      onChange={handleChange}
                      className="bg-white/5 border-white/10 text-white mt-2"
                      placeholder="VIC"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Postcode</Label>
                    <Input
                      name="shipping_postcode"
                      value={profile.shipping_postcode}
                      onChange={handleChange}
                      className="bg-white/5 border-white/10 text-white mt-2"
                      placeholder="3000"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="bg-[#111] border border-white/10 rounded-sm mb-6">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-lg font-bold text-white">Additional Notes</h2>
            </div>
            <div className="p-6">
              <Textarea
                name="notes"
                value={profile.notes}
                onChange={handleChange}
                className="bg-white/5 border-white/10 text-white min-h-[100px]"
                placeholder="Any special requirements or notes..."
              />
            </div>
          </div>

          {/* Terms & Disclaimer Acknowledgment */}
          <div className="bg-[#111] border border-white/10 rounded-sm mb-6">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#CCFF00]" />
                Terms & Disclaimer
              </h2>
            </div>
            <div className="p-6">
              {disclaimerStatus.accepted ? (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-white font-medium">Terms Accepted</p>
                      <Badge className="bg-green-500/10 text-green-400 border-green-500/20">Acknowledged</Badge>
                    </div>
                    <p className="text-white/50 text-sm">
                      You acknowledged and accepted our Terms & Conditions on{' '}
                      <span className="text-white/70">
                        {new Date(disclaimerStatus.accepted_at).toLocaleDateString('en-AU', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </p>
                    <p className="text-white/40 text-xs mt-2">
                      This includes our file responsibility, material properties, lead times, intellectual property, 
                      and limitation of liability policies.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-white font-medium">Terms Not Yet Accepted</p>
                      <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20">Pending</Badge>
                    </div>
                    <p className="text-white/50 text-sm">
                      You will be asked to review and accept our Terms & Conditions when you place your first order.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide rounded-none px-8"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Profile
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CustomerProfile;
